import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAbortError } from "../api/client";
import { fetchErganiHistory, fetchErganiSlots } from "../api/ergani";
import { indexErganiSlots } from "../lib/erganiSlots";
import { isSelectableStore } from "../theme";

export function useErganiSlots({ store, weekStartISO, enabled = true, unifiedLocationId = null }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  const reload = useCallback(async () => {
    abortRef.current?.abort();
    if (!enabled || !isSelectableStore(store) || !weekStartISO) {
      setSlots([]);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const data = await fetchErganiSlots(store, weekStartISO, { signal: controller.signal, unifiedLocationId });
      if (controller.signal.aborted) return;
      setSlots(data.slots || []);
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return;
      setSlots([]);
      setError(err.message || "Ergani load failed");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [enabled, store, weekStartISO, unifiedLocationId]);

  useEffect(() => {
    reload();
    return () => abortRef.current?.abort();
  }, [reload]);

  const byKey = useMemo(() => indexErganiSlots(slots), [slots]);

  const loadHistory = useCallback(async (employeeCode, date) => {
    const data = await fetchErganiHistory(employeeCode, date);
    return data.versions || [];
  }, []);

  return { slots, byKey, loading, error, reload, loadHistory };
}
